import { Injectable, computed, signal } from '@angular/core';
import { ParamMap } from '@angular/router';

/** UUID de sesion anonima devuelto por billetera-core en PUT /start-session. */
export const WIDGET_SESSION_STORAGE_KEY = 'billetera.widget.sessionId';
const TENANT_STORAGE_KEY = 'billetera.widget.tenant';
const ENTRY_STORAGE_KEY = 'billetera.widget.entry';

/**
 * Datos con los que la app anfitriona abre el WebView. El widget no pide
 * identidad: confia en lo que llega por query params.
 */
export interface EntryParams {
  documentId: string;
  gender: string;
  email: string;
  phone: string;
  tenant: string;
  /** Entidad de la app anfitriona. Opcional: viaja al middleware sin interpretarse. */
  entityId: string;
}

const EMPTY_ENTRY: EntryParams = {
  documentId: '',
  gender: '',
  email: '',
  phone: '',
  tenant: '',
  entityId: '',
};

@Injectable({ providedIn: 'root' })
export class SessionStateService {
  private readonly sessionIdSignal = signal<string | null>(null);
  private readonly entrySignal = signal<EntryParams>(this.restoreEntry());

  readonly sessionId = this.sessionIdSignal.asReadonly();
  readonly entry = this.entrySignal.asReadonly();
  readonly documentId = computed(() => this.entrySignal().documentId);
  readonly hasDocument = computed(() => this.entrySignal().documentId.length > 0);
  /** app_token del tenant; el middleware resuelve con el a que nucleo hablar. */
  readonly tenant = computed(() => this.entrySignal().tenant);
  readonly entityId = computed(() => this.entrySignal().entityId);

  setSessionId(id: string): void {
    this.sessionIdSignal.set(id);
    sessionStorage.setItem(WIDGET_SESSION_STORAGE_KEY, id);
  }

  /**
   * Lee los parametros de entrada del WebView. Solo pisa lo guardado cuando la
   * URL trae datos, para no perderlos al navegar entre pantallas.
   */
  readEntryParams(params: ParamMap): EntryParams {
    const documentId = (params.get('dni') || params.get('document_id') || '').trim();
    const gender = (params.get('genero') || params.get('gender') || '').trim();
    const email = (params.get('correo') || params.get('email') || '').trim();
    const rawPhone = (params.get('celular') || params.get('phone') || '').trim();
    const tenant = (params.get('tenant') || '').trim();
    const entityId = (
      params.get('idEntidad') ||
      params.get('id_entidad') ||
      params.get('entity_id') ||
      ''
    ).trim();

    if (!documentId && !gender && !email && !rawPhone && !tenant && !entityId) {
      return this.entrySignal();
    }

    const current = this.entrySignal();
    const entry: EntryParams = {
      documentId: documentId || current.documentId,
      gender: gender || current.gender,
      email: email || current.email,
      phone: rawPhone || current.phone,
      tenant: tenant || current.tenant,
      entityId: entityId || current.entityId,
    };

    this.entrySignal.set(entry);
    sessionStorage.setItem(ENTRY_STORAGE_KEY, JSON.stringify(entry));
    if (entry.tenant) {
      sessionStorage.setItem(TENANT_STORAGE_KEY, entry.tenant);
    }
    return entry;
  }

  reset(): void {
    this.sessionIdSignal.set(null);
    this.entrySignal.set(EMPTY_ENTRY);
    sessionStorage.removeItem(WIDGET_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(ENTRY_STORAGE_KEY);
  }

  private restoreEntry(): EntryParams {
    try {
      const raw = sessionStorage.getItem(ENTRY_STORAGE_KEY);
      return raw ? { ...EMPTY_ENTRY, ...(JSON.parse(raw) as Partial<EntryParams>) } : EMPTY_ENTRY;
    } catch {
      return EMPTY_ENTRY;
    }
  }
}
